import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { FiSend, FiMoreVertical, FiImage } from "react-icons/fi";
import { FaTimes } from "react-icons/fa";
import moment from "moment";
import CommonHeader from "../../../components/header/CommonHeader";
import ConfirmationModal from "../../../components/modal/ConfirmationModal";
import ImageViewer from "../../../components/ImageViewer";
import { appApiHandler } from "../../../api/appApiHandler";
import {
  setMessageHistory,
  setTicketId,
  setTitle,
  setTicketStatus,
} from "../../../store/slices/supportTicketState";
import "./SupportChat.css";
import HomeHeader from "../../../components/header/HomeHeader";
import { showErrorToast } from "../../../utils/toast";
import { getErrorMessage } from "../../../constants/globalFunction";
import { string } from "../../../constants/string";
import { BASE_URL } from "../../../constants/configConstants";
import InlineLoader from "../../../components/loader/InlineLoader";
import { FaUser } from "react-icons/fa";
import EmptyState from "../../../components/EmptyState";
import Breadcrumb from "../../../components/breadcrumb";

const SupportChat = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { ticket_id } = location.state || {};
  const { userData } = useSelector((state) => state.userState);
  const { isLoggedIn } = useSelector((state) => state.authState);
  const { messages, chatTitle, ticketStatus } = useSelector(
    (state) => state.supportTicketState
  );

  const [input, setInput] = useState("");
  const [selectedImages, setSelectedImages] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isImageViewVisible, setIsImageViewVisible] = useState(false);
  const [imageViewImages, setImageViewImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const [menuVisible, setMenuVisible] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const getSupportChat = async (ticket_id) => {
    const reqData = {
      ticket_id: ticket_id,
    };
    const response = await appApiHandler.getTicketChatHistory(reqData);
    if (response?.status === 200) {
      dispatch(setTicketId(response?.data?._id));
      dispatch(setTitle(response?.data?.subject));
      dispatch(setTicketStatus(response?.data?.status));
      dispatch(setMessageHistory(response?.data?.chat || []));
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!ticket_id) {
      navigate("/support-tickets");
      return;
    }

    if (ticket_id) {
      setLoading(true);
      getSupportChat(ticket_id);
    }
  }, [ticket_id, navigate]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Image handling functions
  const handleImageSelect = (event) => {
    const files = Array.from(event.target.files);
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));

    if (imageFiles.length > 0) {
      const imagePreviews = imageFiles.map((file) => ({
        file,
        preview: URL.createObjectURL(file),
        name: file.name,
        size: file.size,
      }));
      setSelectedImages((prev) => [...prev, ...imagePreviews]);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeImage = (index) => {
    setSelectedImages((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      // Revoke object URL to free memory
      URL.revokeObjectURL(prev[index].preview);
      return updated;
    });
  };

  const clearAllImages = () => {
    selectedImages.forEach((img) => URL.revokeObjectURL(img.preview));
    setSelectedImages([]);
  };

  const openImageViewer = (imageUrl) => {
    setImageViewImages([imageUrl]);
    setCurrentImageIndex(0);
    setIsImageViewVisible(true);
  };

  const closeImageViewer = () => {
    setIsImageViewVisible(false);
    setImageViewImages([]);
    setCurrentImageIndex(0);
  };

  const nextImage = () => {
    if (imageViewImages.length > 1) {
      setCurrentImageIndex((prev) => (prev + 1) % imageViewImages.length);
    }
  };

  const previousImage = () => {
    if (imageViewImages.length > 1) {
      setCurrentImageIndex(
        (prev) => (prev - 1 + imageViewImages.length) % imageViewImages.length
      );
    }
  };

  const sendMessage = async () => {
    const hasText = input.trim().length > 0;
    const hasImages = selectedImages.length > 0;

    if (!hasText && !hasImages) return;

    const messageText = input.trim();
    setInput("");

    // Send text message if present
    if (hasText) {
      const reqData = {
        ticket_id: ticket_id,
        message: messageText,
        sender_id: userData?._id,
      };

      try {
        const response = await appApiHandler.addMessageToTicket(reqData);
        if (response?.status === 200) {
          getSupportChat(ticket_id);
        } else {
          setInput(messageText);
          showErrorToast(getErrorMessage(response?.message));
          return;
        }
      } catch (error) {
        setInput(messageText);
        showErrorToast(getErrorMessage(error));
        return;
      }
    }

    // Send images if present
    if (hasImages) {
      setIsUploading(true);
      setUploadProgress(0);

      const totalImages = selectedImages.length;
      let completedImages = 0;

      for (let index = 0; index < selectedImages.length; index++) {
        const imageItem = selectedImages[index];
        try {
          const formData = new FormData();
          formData.append("ticket_id", ticket_id);
          formData.append("sender_id", userData?._id);
          formData.append("image", imageItem.file);

          const response = await appApiHandler.addMessageToTicket(formData);
          if (response?.status !== 200) {
            console.error("Image upload failed:", response);
            showErrorToast(getErrorMessage(response?.message));
          }
        } catch (error) {
          console.error("Error uploading image:", error);
          showErrorToast(getErrorMessage(error));
        } finally {
          completedImages++;
          const progress = (completedImages / totalImages) * 100;
          setUploadProgress(progress);

          if (completedImages === selectedImages.length) {
            // Clear images and refresh chat
            clearAllImages();
            setTimeout(() => {
              getSupportChat(ticket_id);
              setIsUploading(false);
              setUploadProgress(0);
            }, 500);
          }
        }
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const onMorePress = () => {
    setMenuVisible(true);
  };

  const onModalClose = () => {
    setMenuVisible(false);
  };

  const onCloseTicket = () => {
    setMenuVisible(false);
    setConfirmationModal(true);
  };

  const onYesPress = async () => {
    setConfirmationModal(false);
    const reqData = {
      support_ticket_id: ticket_id,
      status: "closed",
    };
    setLoading(true);
    const response = await appApiHandler.updateSupportTicket(reqData);
    setLoading(false);
    if (response?.status === 200) {
      navigate("/support-tickets");
    } else if (response?.message) {
      showErrorToast(getErrorMessage(response?.message));
    } else {
      showErrorToast(string.error.failed_due_to_internal_error);
    }
  };

  const onNoPress = () => {
    setConfirmationModal(false);
  };

  const renderDateSeparator = useCallback((date) => {
    const messageDate = moment(date);
    const today = moment();
    const yesterday = moment().subtract(1, 'day');

    let displayText;

    if (messageDate.isSame(today, 'day')) {
      displayText = 'Today';
    } else if (messageDate.isSame(yesterday, 'day')) {
      displayText = 'Yesterday';
    } else {
      displayText = messageDate.format("D MMMM");
    }

    return (
      <div key={`date-${date}`} className="date-separator">
        <span className="date-separator-text">{displayText}</span>
      </div>
    );
  }, []);

  const renderMessage = useCallback(
    (item) => {
      const isLoggedInUser = item?.sender_id === userData?._id;
      const messageTime = moment(item?.created_at).format("HH:mm");

      const imageUrl = item?.image ? `${BASE_URL}${item.image}` : null;

      return imageUrl ? (
        <div
          key={item?._id}
          className={`image-message-bubble ${isLoggedInUser ? "image-user-bubble" : "image-support-bubble"
            } `}
        >
          <img
            src={imageUrl}
            alt="Message attachment"
            className="message-image"
            onClick={() => openImageViewer(imageUrl)}
            onError={(e) => {
              e.target.style.display = "none";
              console.log("Failed to load image:", imageUrl);
            }}
            loading="lazy"
          />
          <div className="image-message-time">{messageTime}</div>
        </div>
      ) : (
        <div
          key={item?._id}
          className={`message-bubble ${isLoggedInUser ? "user-bubble" : "support-bubble"
            } `}
        >
          {item?.message && (
            <>
              <p className="message-text">{item?.message}</p>
              <div className="message-time">{messageTime}</div>
            </>
          )}
        </div>
      );
    },
    [userData?._id]
  );

  const renderMessagesWithDateSeparators = useCallback(() => {
    if (!messages || messages.length === 0) {
      return (
        <div className="empty-messages">
          <p>No messages yet. Start the conversation!</p>
        </div>
      );
    }

    const groupedMessages = [];
    let currentDate = null;

    messages.forEach((message, index) => {
      const messageDate = moment(message?.created_at).format("YYYY-MM-DD");

      // Add date separator if this is a new date
      if (currentDate !== messageDate) {
        groupedMessages.push(renderDateSeparator(message?.created_at));
        currentDate = messageDate;
      }

      // Add the message
      groupedMessages.push(renderMessage(message));
    });

    return groupedMessages;
  }, [messages, renderDateSeparator, renderMessage]);

  return (
    <div className="support-chat-container">
      {/* Fixed Header Section */}
      <div className="fixed-header">
        <HomeHeader />
        <CommonHeader
          title={chatTitle || "Support Chat"}
          rightComponent={
            ticketStatus === "open" ? (
              <button className="header-more-btn" onClick={onMorePress}>
                <FiMoreVertical size={20} />
              </button>
            ) : null
          }
        />
        <Breadcrumb
          items={[
            {
              label: "Home",
              path: "/",
            },
            {
              label: "Support Tickets",
              path: "/support-tickets",
            },
          ]}
        />
      </div>

      {loading ? (
        <InlineLoader text="Loading chat..." />
      ) : isLoggedIn ? (
        <>
          {menuVisible && (
            <div className="menu-overlay" onClick={onModalClose}>
              <div className="menu-box" onClick={(e) => e.stopPropagation()}>
                <button className="menu-item" onClick={onCloseTicket}>
                  Close Ticket
                </button>
              </div>
            </div>
          )}

          {/* Messages Section */}
          <div className="messages-section">
            <div className="messages-container">
              {renderMessagesWithDateSeparators()}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Fixed Input Section */}
          <div className="fixed-input-section">
            {ticketStatus === "closed" && (
              <div className="ticket-closed-notice">
                <p>This ticket is closed</p>
              </div>
            )}

            {ticketStatus !== "closed" && (
              <>
                {/* Image Preview Section */}
                {selectedImages.length > 0 && (
                  <div className="image-preview-section">
                    <button
                      className="clear-all-images-btn"
                      onClick={clearAllImages}
                    >
                      <FaTimes size={15} />
                    </button>
                    <div className="image-preview-container">
                      {selectedImages.map((imageItem, index) => (
                        <div key={index} className="image-preview-item">
                          <img
                            src={imageItem.preview}
                            alt={`Preview ${index + 1}`}
                            className="image-preview"
                          />
                          <button
                            className="remove-image-btn"
                            onClick={() => removeImage(index)}
                          >
                            <FaTimes size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Upload Progress */}
                {isUploading && (
                  <div className="upload-progress-section">
                    <div className="upload-progress-bar">
                      <div
                        className="upload-progress-fill"
                        style={{ width: `${uploadProgress}%` }}
                      ></div>
                    </div>
                    <span className="upload-progress-text">
                      Uploading... {Math.round(uploadProgress)}%
                    </span>
                  </div>
                )}

                {/* Input Container */}
                <div className="input-container">
                  <button
                    className="action-btn gallery-btn"
                    onClick={() => fileInputRef.current?.click()}
                    title="Select from Gallery"
                  >
                    <FiImage size={20} />
                  </button>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageSelect}
                    style={{ display: "none" }}
                  />

                  <textarea
                    className="message-input"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Type your message..."
                    onKeyPress={handleKeyPress}
                    rows={1}
                  />

                  <button
                    className={`send-btn ${input.trim() || selectedImages.length > 0 ? "active" : ""
                      }`}
                    onClick={sendMessage}
                    disabled={!input?.trim() && selectedImages.length === 0}
                  >
                    <FiSend size={18} />
                  </button>
                </div>
              </>
            )}
          </div>
        </>
      ) : (
        <EmptyState
          icon={FaUser}
          title="Sign In to Continue"
          description="please login with your account. Enjoy a seamless shopping experience with personalized recommendations and more!"
          buttonText="Login"
          onButtonClick={() => navigate("/login")}
          iconSize={48}
        />
      )}

      <ConfirmationModal
        visible={confirmationModal}
        title="Close Ticket"
        description="Are you sure you want to close this ticket? This action cannot be undone."
        rightButtonText="Close Ticket"
        leftButtonText="Cancel"
        onRightButtonPress={onYesPress}
        onLeftButtonPress={onNoPress}
      />

      <ImageViewer
        images={imageViewImages}
        currentIndex={currentImageIndex}
        isVisible={isImageViewVisible}
        onClose={closeImageViewer}
        onNext={nextImage}
        onPrevious={previousImage}
      />
    </div>
  );
};

export default SupportChat;
